import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicModal from "@components/core/modals/BasicModal";
import BasicButton from "@components/core/buttons/BasicButton";
import { Checkbox, Form, Input } from "antd";
import { Guest } from "@type/core/offices/offices.types";
import { toastError } from "@utils/helpers/toast-helper";
import { formRules, formValidateTriggers } from "@/utils/Constant";

interface FormValues {
  email: string;
  delegations: string[];
}

interface GuestModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk: (values: FormValues) => void;
  title: string;
  delegations: { value: string; label: string }[];
  guest?: Guest;
}

const GuestModal: FunctionComponent<GuestModalProps> = ({
  visible,
  onCancel,
  onOk,
  title,
  delegations,
  guest,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [selectedNewDelegations, setSelectedNewDelegations] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (guest?.delegations) {
      setSelectedNewDelegations(guest.delegations);
      form.setFieldValue("email", guest.email ?? "");
    }
  }, [guest]);

  const resetForm = () => {
    form.resetFields();
    setSelectedNewDelegations([]);
  };

  const handleCancel = () => {
    onCancel();
    resetForm();
  };

  const validateNewDelegations = () => {
    if (selectedNewDelegations.length === 0) {
      toastError(t("error.form.mandatoryDelegation"));
      return false;
    }

    return true;
  };

  const handleOk = () => {
    form
      .validateFields()
      .catch((e) => {
        // This allow to catch both validation at same time instead of first email, then delegations
        validateNewDelegations();

        throw e;
      })
      .then((values: FormValues) => {
        if (!validateNewDelegations()) {
          return;
        }

        values.delegations = selectedNewDelegations;

        onOk(values);
        resetForm();
      })
      .catch(() => {
        // Errors toasts are self managed by rules themselves inside .validateFields()
      });
  };

  const handleCheckboxChange = (value: string, checked: boolean) => {
    if (checked) {
      setSelectedNewDelegations([...selectedNewDelegations, value]);
    } else {
      setSelectedNewDelegations(
        selectedNewDelegations.filter((item) => item !== value),
      );
    }
  };

  return (
    <BasicModal
      visible={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      title={title}
      closable={false}
      footer={
        <div className="modal-footer-buttons">
          <BasicButton
            variant="secondary"
            text={t("buttons.no")}
            onClick={handleCancel}
          />
          <BasicButton
            variant="primary"
            text={t("buttons.yes")}
            onClick={handleOk}
          />
        </div>
      }
      className={`app-modal-confirm ${visible ? "" : "modal-empty"}`}
    >
      <Form form={form} layout="vertical" {...formValidateTriggers}>
        <Form.Item
          name="email"
          label={t("offices.users.guests.headers.email")}
          rules={formRules.email(false)}
          required
        >
          <Input />
        </Form.Item>
        <Form.Item label={t("offices.users.guests.headers.delegations")}>
          {delegations.map((delegation) => (
            <Checkbox
              key={delegation.value}
              value={delegation.value}
              checked={selectedNewDelegations.includes(delegation.value)}
              onChange={(e) =>
                handleCheckboxChange(delegation.value, e.target.checked)
              }
            >
              {delegation.label}
            </Checkbox>
          ))}
        </Form.Item>
      </Form>
    </BasicModal>
  );
};

export default GuestModal;
